import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { Brand } from "@/components/Brand";
import { CrawlableLink } from "@/components/CrawlableLink";
import { EASE } from "./Reveal";
import { TextSizeControl } from "@/components/accessibility/TextSizeControl";

export const NAV = [
  {
    label: "Discover",
    id: "discover",
    items: [
      { label: "Discover Mary", to: "#discover", desc: "Scripture, the four dogmas, the saints" },
      { label: "Marian Apologetics", to: "/apologetics", desc: "Answering questions with the Church" },
      { label: "East & West", to: "/east-and-west", desc: "Theotokos in Latin and Eastern Catholic traditions" },
      { label: "Sacred Art & Icons", to: "/sacred-art", desc: "Reading the image of the Mother of God" },
      { label: "Saints & Witnesses", to: "/saints", desc: "Lives shaped by devotion to Mary" },
      { label: "Library", to: "/library", desc: "Documents, texts, and further reading" },
      { label: "Parish Poster Kit", to: "/parish-poster-kit", desc: "Printable overviews and full apparition stories" },
    ],
  },
  { label: "Apparitions & Shrines", id: "apparitions", to: "/apparitions" },
  {
    label: "Christos",
    id: "christos",
    items: [
      { label: "Christos · King of Glory", to: "/christ-the-king", desc: "To Jesus through Mary — the King of Glory" },
      { label: "Dogmas of Jesus", to: "/christ-the-king/dogmas", desc: "Divinity, Incarnation, Redemption, Resurrection, Kingship" },
      { label: "The Sacred Heart of Jesus", to: "/christ-the-king/sacred-heart", desc: "St. Margaret Mary, Paray-le-Monial, the Twelve Promises" },
      { label: "The Divine Mercy", to: "/christ-the-king/divine-mercy", desc: "St. Faustina, the Image, the Chaplet, the Feast" },
      { label: "The Two Hearts", to: "/christ-the-king/two-hearts", desc: "The Sacred Heart and the Immaculate Heart, united" },
      { label: "The Holy Face", to: "/christ-the-king/holy-face", desc: "The face of the Man of Sorrows" },
      { label: "Eucharistic Miracles", to: "/christ-the-king/eucharistic-miracles", desc: "The Real Presence and its signs" },
      { label: "Christ the King", to: "/christ-the-king/sovereignty", desc: "Quas Primas and the reign of Christ" },
    ],
  },
  {
    label: "Prayer & Devotion",
    id: "pray",
    items: [
      { label: "Prayer treasury", to: "/prayer-and-devotion", desc: "Latin and Eastern Marian prayers, side by side" },
      { label: "The Holy Rosary", to: "/rosary", desc: "Step-by-step with all four mysteries" },
      { label: "First Fridays · Jesus", to: "/christ-the-king/sacred-heart#first-fridays", desc: "Nine months with the Sacred Heart" },
      { label: "First Saturdays · Mary", to: "/prayer-and-devotion/first-saturdays", desc: "Five months with the Immaculate Heart" },
      { label: "Divine Mercy Chaplet", to: "/christ-the-king/divine-mercy#chaplet", desc: "Pray in English, Spanish or French" },
      { label: "The Prayer Rope", to: "/prayer-rope", desc: "The Jesus Prayer in the Christian East" },
      { label: "Totus Tuus · 33 days", to: "/christ-the-king/consecration", desc: "To Jesus through Mary · Consecration preparation" },
      { label: "Home Enthronement", to: "/christ-the-king/enthronement", desc: "The Sacred Heart · A printable family ceremony" },
    ],
  },
  { label: "Feasts & Calendar", id: "calendar", to: "/calendar" },
  {
    label: "About",
    id: "about",
    items: [
      { label: "About the treasury", to: "/about", desc: "Mission, emblem, and the road ahead" },
      { label: "Editorial Standards", to: "/editorial-standards", desc: "Sourcing, status labels, discernment" },
      { label: "Contact", to: "/contact", desc: "Corrections, research, questions" },
      { label: "Privacy Policy", to: "/privacy", desc: "What we keep and what we never sell" },
    ],
  },
];

// Prefer the specific prayer link over its broader Christos parent route.
const activeGroup = (pathname, hash) => NAV.flatMap((group) =>
  (group.items || [group]).map((link) => ({ id: group.id, to: link.to }))
).filter(({ to }) => {
  if (!to || to.startsWith("#")) return false;
  const [path, anchor] = to.split("#");
  return (pathname === path || pathname.startsWith(`${path}/`)) && (!anchor || hash === `#${anchor}`);
}).sort((a, b) => b.to.length - a.to.length)[0]?.id;

function Dropdown({ item, active }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        data-testid={`nav-${item.id}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-current={active ? "true" : undefined}
        className={`group relative flex items-center gap-1 py-2 text-sm font-medium tracking-wide transition-colors hover:text-marian ${
          active ? "text-marian" : "text-charcoal"
        }`}
      >
        {item.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        <span className={`absolute -bottom-0.5 left-0 h-px bg-gold transition-all duration-300 ${open ? "w-full" : "w-0 group-hover:w-full"}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.3, ease: EASE }}
            data-testid={`nav-dropdown-${item.id}`}
            className="absolute left-0 top-full w-80 pt-3"
          >
            <div className="overflow-hidden rounded-2xl border border-marian/15 bg-ivory shadow-[0_30px_60px_-30px_rgba(16,42,67,0.5)]">
              <div className="gold-rule" />
              <ul className="max-h-[calc(100vh-150px)] overflow-y-auto p-2" data-lenis-prevent>
                {item.items.map((sub) => (
                  <li key={sub.label}>
                    <CrawlableLink
                      to={sub.to}
                      data-testid={`nav-sub-${sub.to.replace(/[^a-z-]/g, "") || "discover"}`}
                      onNavigate={() => setOpen(false)}
                      className="block w-full rounded-xl px-4 py-3 text-left transition-colors hover:bg-paleblue/70"
                    >
                      <span className="block font-serif text-lg font-semibold text-midnight">{sub.label}</span>
                      <span className="mt-0.5 block text-xs text-slateblue">{sub.desc}</span>
                    </CrawlableLink>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();
  const activeId = activeGroup(pathname, hash);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);


  return (
    <header
      data-testid="main-header"
      className={`sticky top-0 z-50 border-b transition-all duration-500 ${
        scrolled
          ? "border-marian/15 bg-ivory/85 shadow-[0_12px_40px_-24px_rgba(16,42,67,0.4)] backdrop-blur-xl"
          : "border-transparent bg-ivory/60 backdrop-blur-sm"
      }`}
    >
      <div className="site-header-bar mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2.5 sm:px-8 lg:px-16">
        <Brand />

        <nav className="site-primary-nav hidden items-center gap-4 xl:flex" aria-label="Primary">
          {NAV.map((item) =>
            item.items ? (
              <Dropdown key={item.id} item={item} active={activeId === item.id} />
            ) : (
              <CrawlableLink
                key={item.id}
                to={item.to}
                data-testid={`nav-${item.id}`}
                aria-current={activeId === item.id ? "page" : undefined}
                onNavigate={() => setOpen(false)}
                className={`group relative py-2 text-sm font-medium tracking-wide transition-colors hover:text-marian ${
                  activeId === item.id ? "text-marian" : "text-charcoal"
                }`}
              >
                {item.label}
                <span
                  className={`absolute -bottom-0.5 left-0 h-px bg-gold transition-all duration-300 ${
                    activeId === item.id ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </CrawlableLink>
            )
          )}
          <CrawlableLink
            to="/rosary"
            data-testid="header-cta-rosary"
            onNavigate={() => setOpen(false)}
            className="rounded-full bg-marian px-5 py-2.5 text-sm font-semibold text-ivory shadow-[0_10px_24px_-12px_rgba(31,78,121,0.8)] transition-all duration-300 hover:bg-midnight hover:shadow-[0_14px_28px_-12px_rgba(16,42,67,0.9)]"
          >
            Pray the Rosary
          </CrawlableLink>
        </nav>

        <TextSizeControl />
        <button
          type="button"
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          className="site-menu-button rounded-md p-2 text-midnight xl:hidden"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="site-mobile-menu max-h-[70dvh] overflow-y-auto border-t border-marian/10 bg-ivory xl:hidden"
            aria-label="Mobile"
            data-testid="mobile-menu"
          >
            <div className="space-y-4 px-6 py-6">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.id}
                  data-testid={`mobile-nav-group-${item.id}`}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35, ease: EASE }}
                >
                  {item.items ? (
                    <>
                      <p data-testid={`mobile-nav-heading-${item.id}`} className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{item.label}</p>
                      <ul className="mt-2 space-y-1">
                        {item.items.map((sub) => (
                          <li key={sub.label}>
                            <CrawlableLink
                              to={sub.to}
                              data-testid={`mobile-nav-sub-${sub.to.replace(/[^a-z-]/g, "") || "discover"}`}
                              onNavigate={() => setOpen(false)}
                              className="block w-full py-1.5 text-left font-serif text-xl font-medium text-midnight"
                            >
                              {sub.label}
                            </CrawlableLink>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <CrawlableLink
                      to={item.to}
                      data-testid={`mobile-nav-${item.id}`}
                      onNavigate={() => setOpen(false)}
                      className="block w-full py-1.5 text-left font-serif text-2xl font-semibold text-midnight"
                    >
                      {item.label}
                    </CrawlableLink>
                  )}
                </motion.div>
              ))}
              <CrawlableLink
                to="/rosary"
                data-testid="mobile-cta-rosary"
                onNavigate={() => setOpen(false)}
                className="mt-2 block w-full rounded-full bg-marian py-3 text-center text-sm font-semibold text-ivory"
              >
                Pray the Rosary
              </CrawlableLink>
              <Link to="/editorial-standards" data-testid="mobile-editorial-link" className="block pt-2 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-slateblue">
                Editorial standards
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
